import { Gpt } from '@pcln/ad-unit'
import { Divider, Heading, P } from '@pcln/horizon'
import { SearchFormCard } from '@pcln/horizon-search-form'
import { themeGet } from '@styled-system/theme-get'
import { Box, Card, Container, Flex, Hide, Text } from 'pcln-design-system'
import { type ReactNode } from 'react'
import styled from 'styled-components'

import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import { HeroRedesignLayout } from '@/components/Heros/HeroRedesignLayout'

import ConditionalRender, { Experiment, Original } from '../../ConditionalRender'
import HeroImageParallax from '../../Heros/HeroImageParallax'
import Content from '../Content'
import HorizonPackagesForm from '../HorizonForm/HorizonPackagesForm'
import PackagesSearchForm from '../SearchForm/Form'
import GenericErrorBanner from '../SearchForm/GenericErrorBanner'
import { useVacationVibeEntry } from '../SearchForm/useVacationVibeEntry'
import { onSubmit } from '../SearchForm/utils'
import usePackagesLandingFormContent from '../usePackagesLandingFormContent'

const BlockFlex = styled(Box)`
  position: relative;
  z-index: 1;
  ${themeGet('mediaQueries.xxl')} {
    display: flex;
  }
`

const HideAdUnit = styled(Hide)`
  flex: none;
  text-align: center;
  position: relative;
  margin: 16px;
  min-height: 90px;
  ${themeGet('mediaQueries.xxl')} {
    width: 300px;
    margin: 0 16px 0 16px;
  }
`

const footerText = 'Save up to $625 when you book your hotel and flight together!'

function LandingSavingsFooter({ children }: { children: ReactNode }) {
  const showVacationVibeEntry = useVacationVibeEntry({ shouldFireImpression: false })

  if (showVacationVibeEntry) return null

  return children
}

const PACKAGES_COMPANION_AD = <Gpt render={() => <div id='div-gpt-ad-1511925695047-0' />} />
const PACKAGES_COMPANION_AD_VARIANT = <Gpt render={() => <div id='div-gpt-ad-1790171268824-0' />} />

function HorizonPackagesLandingForm() {
  const { getHeadline, shouldShowErrorBanner } = usePackagesLandingFormContent('landing')

  return (
    <SearchFormCard data-testid='hero-search-form' variant='panel' width='full'>
      <Heading
        as='h1'
        palette='primary'
        shade='12'
        textStyle='heading3'
        className='m-0 pb-1 text-heading3 lg:text-heading2'
      >
        {getHeadline()}
      </Heading>
      {shouldShowErrorBanner && (
        <div className='mb-3'>
          <GenericErrorBanner />
        </div>
      )}
      <HorizonPackagesForm onSubmit={onSubmit} pageName='landing' />
      <LandingSavingsFooter>
        <div
          className='-mx-4 mt-4 -mb-4 overflow-hidden rounded-b-lg bg-neutral-1'
          data-testid='horizon-packages-footer'
        >
          <Divider lineSlotBeforeClassName='my-0' />
          <div className='flex justify-center bg-neutral-1 p-4'>
            <P textStyle='body2' palette='neutral' shade='12' align='center' className='m-0'>
              {footerText}
            </P>
          </div>
        </div>
      </LandingSavingsFooter>
    </SearchFormCard>
  )
}

function PackagesHeroSearchForm() {
  return (
    <ComponentMetricsBoundary componentId='SearchFormPackages'>
      <ConditionalRender experimentCode='USB_SEARCH_PCKG_LANDING_HORIZON'>
        <Original>
          <Card
            data-testid='hero-search-form'
            borderWidth={0}
            color='background.lightest'
            borderRadius='2xl'
            boxShadowSize='xl'
            width={1}
          >
            <Box p={3}>
              <PackagesSearchForm onSubmit={onSubmit} pageName='landing' />
            </Box>
            <LandingSavingsFooter>
              <Divider lineSlotBeforeClassName='my-0' />
              <Flex
                p={3}
                borderRadius='2xl'
                boxShadowSize='xl'
                rounded='bottom'
                color='background.light'
                justifyContent='center'
              >
                <Text fontSize={1} textAlign='center' color='text.dark'>
                  {footerText}
                </Text>
              </Flex>
            </LandingSavingsFooter>
          </Card>
        </Original>
        <Experiment>
          <HorizonPackagesLandingForm />
        </Experiment>
      </ConditionalRender>
    </ComponentMetricsBoundary>
  )
}

function PackagesHeroContent() {
  return (
    <Container maxWidth={1280}>
      <BlockFlex mx={3} py={[3, null, null, null, 5]} pb={3}>
        <Box width={1}>
          <PackagesHeroSearchForm />
        </Box>
        <HideAdUnit xs sm>
          {PACKAGES_COMPANION_AD}
        </HideAdUnit>
      </BlockFlex>
    </Container>
  )
}

function PackagesHeroRedesign() {
  return (
    <HeroRedesignLayout
      adBreakpoint='xl'
      adMinHeightClass='min-h-[90px]'
      adUnit={PACKAGES_COMPANION_AD_VARIANT}
      outerClassName='relative mb-8'
    >
      <PackagesHeroSearchForm />
    </HeroRedesignLayout>
  )
}

function Desktop() {
  return (
    <Box data-testid='packages-desktop'>
      <ConditionalRender experimentCode='CG_PRD_HERO_REDESIGN'>
        <Original>
          <HeroImageParallax
            isScrollable
            gptId='div-gpt-ad-1511925223449-0'
            slot='/3102/priceline.dart/vp_homepage_hero_image'
            data-testid='pcln-hero-image-parallax'
            mb={4}
          >
            <PackagesHeroContent />
          </HeroImageParallax>
        </Original>
        <Experiment>
          <PackagesHeroRedesign />
        </Experiment>
      </ConditionalRender>
      <Hide xs sm color='background.lightest'>
        <Content />
      </Hide>
    </Box>
  )
}

export default Desktop
